import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import {
  Loader2, Users, Activity, GitBranch, ListChecks, Megaphone, CalendarClock,
  LayoutTemplate, KeyRound, Images, Link2, UsersRound, Building2, ExternalLink, Wallet, ShieldAlert, Award, GanttChartSquare, Flag,
} from "lucide-react";
import { INP_STYLE } from "./client/shared";
import OverviewTab from "./client/OverviewTab";
import AccessTab from "./client/AccessTab";
import GalleryTab from "./client/GalleryTab";
import DocumentsTab from "./client/DocumentsTab";
import TeamTab from "./client/TeamTab";
import ClientsManager from "./client/ClientsManager";
import { StatusPanel, TimelinePanel, ActionsPanel, UpdatesPanel, MeetingsPanel } from "./client/GovernancePanels";
import { BudgetPanel, RisksPanel, QualityPanel } from "./client/RegistryPanels";
import { SchedulePanel } from "./client/SchedulePanel";
import { MilestonesPanel } from "./client/MilestonesPanel";

// Grupate vizual pe 2 rânduri (fără etichete), separate cu un divider subtil.
const TAB_ROWS = [
  [
    [
      ["overview", "Configurare portal", LayoutTemplate],
      ["access", "Acces utilizatori", KeyRound],
      ["gallery", "Galerie", Images],
      ["documents", "Documente", Link2],
      ["team", "Echipă", UsersRound],
    ],
  ],
  [
    [
      ["status", "Status & Health", Activity],
      ["timeline", "Timeline", GitBranch],
      ["milestones", "Repere", Flag],
      ["schedule", "Grafic execuție", GanttChartSquare],
      ["actions", "Decizii & Aprobări", ListChecks],
      ["updates", "Update-uri", Megaphone],
      ["meetings", "Întâlniri", CalendarClock],
    ],
    [
      ["budget", "Buget & costuri", Wallet],
      ["risks", "Riscuri", ShieldAlert],
      ["quality", "Calitate", Award],
    ],
  ],
];

export default function ClientConfig() {
  const { pid: routePid } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState(null);
  const [pid, setPid] = useState(routePid || "");
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [mode, setMode] = useState("project"); // project | clients

  async function loadMeta() {
    try { const { data } = await api.get("/staff/client-admin/meta"); setMeta(data); } catch { /* */ }
  }
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/projects"); setProjects(data || []); }
      catch (e) { toast.error(apiErr(e)); }
      loadMeta();
    })();
  }, []);

  async function load(id) {
    setPid(id);
    if (!id) { setCfg(null); navigate("/staff/client", { replace: true }); return; }
    navigate(`/staff/client/${id}`, { replace: true });
    setLoading(true);
    try { const { data } = await api.get(`/staff/client-config/${id}`); setCfg(data); }
    catch (e) { toast.error(apiErr(e)); setCfg(null); } finally { setLoading(false); }
  }
  useEffect(() => { if (routePid) load(routePid); /* eslint-disable-next-line */ }, []);

  const reload = () => load(pid);
  const accessCount = (cfg?.project?.client_user_ids || []).length;

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <div className="h-11 w-11 rounded-xl bg-brand/10 flex items-center justify-center"><Users className="h-6 w-6 text-brand" /></div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-brand">Portal client</p>
            <h1 className="font-display font-semibold text-[32px] tracking-[-0.02em] leading-tight text-ink">Client</h1>
          </div>
          <div className="ml-auto flex gap-1 bg-neutral-100 rounded-lg p-1">
            <button onClick={() => setMode("project")} data-testid="cc-mode-project"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-md ${mode === "project" ? "bg-white text-ink shadow-sm font-medium" : "text-ink-muted hover:text-ink"}`}>
              <LayoutTemplate className="h-3.5 w-3.5" /> Configurare proiect
            </button>
            <button onClick={() => setMode("clients")} data-testid="cc-mode-clients"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-md ${mode === "clients" ? "bg-white text-ink shadow-sm font-medium" : "text-ink-muted hover:text-ink"}`}>
              <Building2 className="h-3.5 w-3.5" /> Clienți & utilizatori
            </button>
          </div>
        </div>

        {mode === "clients" ? (
          <ClientsManager projects={projects} onChanged={() => { loadMeta(); if (pid) load(pid); }} />
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-end gap-4">
              <div className="min-w-[360px] max-w-md flex-1">
                <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted block mb-1">Proiect</label>
                <select value={pid} onChange={e => load(e.target.value)} data-testid="cc-project"
                  className="w-full border border-neutral-300 px-3 py-2.5 text-sm bg-white rounded-lg">
                  <option value="">— Alege un proiect —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ""}</option>)}
                </select>
              </div>
              {cfg && (
                <div className="flex items-center gap-4 text-[12px] text-ink-muted pb-2.5" data-testid="cc-summary">
                  <span>Companie: <b className="text-ink">{cfg.company?.name || "—"}</b></span>
                  <span>Utilizatori cu acces: <b className={accessCount ? "text-ink" : "text-amber-600"}>{accessCount}</b></span>
                  <span>Status: <b className="text-ink">{cfg.client_status}</b></span>
                  <a href={`/portal/proiecte/${pid}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline" title="Necesită cont client"><ExternalLink className="h-3 w-3" /> URL portal</a>
                </div>
              )}
            </div>

            {loading ? <div className="flex items-center gap-2 text-ink-muted"><Loader2 className="h-5 w-5 animate-spin" /> Se încarcă...</div>
              : !cfg ? <div className="py-16 text-center text-ink-muted border border-dashed border-neutral-200 rounded-xl">Alege un proiect pentru a configura ce vede clientul.</div>
              : (
              <>
                {accessCount === 0 && (
                  <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-[13px] text-amber-800" data-testid="cc-no-access-warning">
                    Niciun utilizator-client nu are acces la acest proiect. Configurează accesul în tab-ul „Acces utilizatori”.
                  </div>
                )}
                <div className="mb-6 space-y-2">
                  {TAB_ROWS.map((row, ri) => (
                    <div key={ri} className="flex flex-wrap items-center gap-2">
                      {row.map((group, gi) => (
                        <React.Fragment key={gi}>
                          {gi > 0 && <div className="hidden md:block h-6 w-px bg-neutral-200 mx-1" aria-hidden />}
                          <div className="flex gap-1 bg-neutral-100 rounded-lg p-1">
                            {group.map(([k, label, Icon]) => (
                              <button key={k} onClick={() => setTab(k)} data-testid={`cc-tab-${k}`}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-md whitespace-nowrap transition-colors ${tab === k ? "bg-white text-ink shadow-sm font-medium" : "text-ink-muted hover:text-ink"}`}>
                                <Icon className="h-3.5 w-3.5" /> {label}
                              </button>
                            ))}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  ))}
                </div>

                {tab === "overview" && <OverviewTab key={pid + cfg.last_update} pid={pid} cfg={cfg} meta={meta} onSaved={setCfg} />}
                {tab === "access" && <AccessTab pid={pid} cfg={cfg} meta={meta} reloadMeta={loadMeta} onSaved={setCfg} />}
                {tab === "gallery" && <GalleryTab pid={pid} meta={meta} onCoverChanged={reload} />}
                {tab === "documents" && <DocumentsTab pid={pid} meta={meta} />}
                {tab === "team" && <TeamTab pid={pid} cfg={cfg} meta={meta} onSaved={setCfg} />}
                {tab === "status" && <StatusPanel key={pid + cfg.last_update} pid={pid} cfg={cfg} onSaved={setCfg} />}
                {tab === "timeline" && <TimelinePanel key={pid + cfg.last_update} pid={pid} cfg={cfg} onSaved={setCfg} />}
                {tab === "milestones" && <MilestonesPanel pid={pid} reload={reload} />}
                {tab === "schedule" && <SchedulePanel key={pid + (cfg.schedule?.uploaded_at || "none")} pid={pid} cfg={cfg} reload={reload} />}
                {tab === "actions" && <ActionsPanel pid={pid} cfg={cfg} reload={reload} />}
                {tab === "updates" && <UpdatesPanel pid={pid} cfg={cfg} reload={reload} />}
                {tab === "meetings" && <MeetingsPanel pid={pid} cfg={cfg} reload={reload} />}
                {tab === "budget" && <BudgetPanel key={pid + cfg.last_update} pid={pid} cfg={cfg} onSaved={setCfg} reload={reload} />}
                {tab === "risks" && <RisksPanel pid={pid} cfg={cfg} reload={reload} />}
                {tab === "quality" && <QualityPanel pid={pid} cfg={cfg} reload={reload} />}
              </>
            )}
          </>
        )}
      </div>
      <style>{INP_STYLE}</style>
    </PortalLayout>
  );
}
